const EventEmitter = require("events");
const OpusMediaRecorder = require("opus-media-recorder");

const log = require("./log");

module.exports = (options = {}) => {
	const rawMode = options.raw || false;

	const recorder = {
		_events: new EventEmitter(),
		_mediaRecorder: undefined,
		_stream: undefined,
		_micStream: undefined,
		_recCtx: undefined,

		getMediaRecorder: (stream) => {
			if(window.MediaRecorder && window.MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")) {
				log.log("Using native MediaRecorder (ogg/opus)");
				return new window.MediaRecorder(stream, { mimeType: "audio/ogg; codecs=opus" });
			}

			// Log supported formats for debugging
			if(window.MediaRecorder) {
				const formats = ['audio/webm', 'audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg'];
				const supported = formats.filter(f => window.MediaRecorder.isTypeSupported(f));
				console.log('Supported formats:', supported);
			}

			log.warning("Using OpusMediaRecorder polyfill - for best experience, use Firefox");

			const workerOptions = {
				encoderWorkerFactory: function () {
					return new Worker(new URL("../node_modules/opus-media-recorder/encoderWorker.umd.js", import.meta.url))
				},
				OggOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/OggOpusEncoder.wasm',
				WebMOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/WebMOpusEncoder.wasm'
			};

			return new OpusMediaRecorder(stream, { mimeType: "audio/ogg; codecs=opus" }, workerOptions);
		},

		getLocalStream: () => {
			if(!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
				log.error("Microphone not supported");
				return Promise.reject(new Error("mediaDevices not supported"));
			}

			const requestStream = () => navigator.mediaDevices
				.getUserMedia({
					video: false,
					audio: {
						channelCount: 1,
						sampleRate: 24000,
						noiseSuppression: true,
						echoCancellation: true,
						autoGainControl: true,
						voiceIsolation: true
					}
				})
				.then((stream) => {
					const track = stream.getAudioTracks()[0];
					const settings = track.getSettings();
					const micSampleRate = settings.sampleRate || 48000;
					log.log(`Audio: ${micSampleRate}Hz, ${settings.channelCount || '?'}ch`);

					const features = [
						settings.noiseSuppression ? 'Noise Suppression' : null,
						settings.echoCancellation ? 'Echo Cancellation' : null,
						settings.autoGainControl ? 'Auto Gain' : null,
						settings.voiceIsolation ? 'Voice Isolation' : null
					].filter(Boolean);
					log.log(`Features: ${features.length ? features.join(', ') : 'none'}`);

					// Use mic's sample rate - Firefox doesn't allow mismatched rates
					const recCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: micSampleRate });
					recorder._recCtx = recCtx;

					// Raw mode: bypass all DSP
					if(rawMode) {
						log.log('Raw mode: DSP bypassed');
						recorder._micStream = stream;
						return Promise.resolve(stream);
					}

					// Audio chain: source → bandpass → gate → compressor → gain → output
					const source = recCtx.createMediaStreamSource(stream);

					// Bandpass filter (300-3400Hz voice band)
					const highpass = recCtx.createBiquadFilter();
					highpass.type = 'highpass';
					highpass.frequency.value = 300;

					const lowpass = recCtx.createBiquadFilter();
					lowpass.type = 'lowpass';
					lowpass.frequency.value = 3400;

					const compressor = recCtx.createDynamicsCompressor();
					compressor.threshold.value = -50;
					compressor.knee.value = 6;
					compressor.ratio.value = 20;
					compressor.attack.value = 0.001;
					compressor.release.value = 0.1;

					// Makeup gain to boost compressed signal back up
					const makeupGain = recCtx.createGain();
					makeupGain.gain.value = 10; // ~20dB boost

					const destination = recCtx.createMediaStreamDestination();

					// Try to add noise gate (requires AudioWorklet support)
					return recCtx.audioWorklet.addModule(new URL('./gate-processor.js', import.meta.url))
						.then(() => {
							const gate = new AudioWorkletNode(recCtx, 'gate-processor');
							source.connect(highpass);
							highpass.connect(lowpass);
							lowpass.connect(gate);
							gate.connect(compressor);
							compressor.connect(makeupGain);
							makeupGain.connect(destination);
							log.log('Noise gate enabled');
							return destination.stream;
						})
						.catch((err) => {
							// Fallback: no gate
							console.warn('AudioWorklet not supported, skipping noise gate:', err);
							source.connect(highpass);
							highpass.connect(lowpass);
							lowpass.connect(compressor);
							compressor.connect(makeupGain);
							makeupGain.connect(destination);
							return destination.stream;
						})
						.finally(() => {
							// Store original stream for cleanup
							recorder._micStream = stream;
						});
				});

			return requestStream()
				.catch((err) => {
					log.warning("Retrying microphone access...");
					return requestStream();
				})
				.catch((err) => {
					console.error(`you got an error: ${err}`);
					log.error("Microphone access failed");
				});
		},

		record: () => {
			return recorder.getLocalStream().then(stream => {
				return new Promise((resolve) => {
					const mediaRecorder = recorder.getMediaRecorder(stream);

					mediaRecorder.onerror = (e) => {
						const errorDetails = e.error
							? `${e.error.name}: ${e.error.message}`
							: JSON.stringify(e);
						log.error(`MediaRecorder error: ${errorDetails}`);
						console.error('MediaRecorder error event:', e);
					};

					mediaRecorder.onstart = () => {
						resolve();
					};

					mediaRecorder.ondataavailable = (e) => {
						if(e.data.size === 0) {
							return;
						}

						recorder._events.emit("samples", e.data);
					};

					recorder._mediaRecorder = mediaRecorder;
					recorder._stream = stream;

					mediaRecorder.start(10);
				});
			});
		},

		stop: () => {
			if(recorder._mediaRecorder && recorder._mediaRecorder.state === "recording") {
				recorder._mediaRecorder.stop();

				recorder._stream.getTracks().forEach(function(track) {
					track.stop();
				});

				// Also stop original mic stream
				if(recorder._micStream) {
					recorder._micStream.getTracks().forEach(function(track) {
						track.stop();
					});
				}

				// Close recording AudioContext
				if(recorder._recCtx) {
					recorder._recCtx.close();
				}
			}
		},

		on: (...args) => {
			return recorder._events.on(...args);
		}
	};

	return recorder;
};
